/*  iceberg-ui  */
import { Icon, IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    AssignmentTurnedIn,
    NoteAltOutlined,
    Search
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    TableCell,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { Sources } from 'application/Shared/Components/Avatars';
import { SSOIcons } from 'application/Shared/Components/Avatars/SSO';
import { DataCard } from 'application/Shared/Components/Cards/Components/DataCard';
import { Mock } from 'application/Shared/Components/Core';
import { AppHeader } from 'application/Shared/Components/Headers/AppHeader';
import {
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TagLinksList } from 'application/Shared/Components/Tag';
import { Rank, Usage } from 'application/Shared/Components/Usage';
import { FontSizes } from 'application/Shared/Constants';
import { TenantAppContextProvider } from 'application/Shared/Contexts/TenantAppContext';
import {
    Helpers,
    Notify,
    Payload,
    Response,
    Transition
} from 'application/Shared/Helpers';
import { Note, TenantApp } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import i18n from 'i18n';

/*  Objects  */
import {
    Application,
    ApplicationCategoryAndSubCategory,
    ApplicationDescription
} from 'objects/Application';
import NotesModal from 'objects/Application/NotesModal';
import { User, UserEmail, UserIconAndName } from 'objects/User';

/*  React  */
import React, { Component, useMemo } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/*  Modals  */
import { EditTagLinksModal } from './globalTag/EditTagLinksModal';

const headers = [
    {
        align: 'left',
        field: 'rank',
        renderHeader: <strong>{i18n.t('Common.rank')}</strong>,
        width: '5%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{i18n.t('Common.user')}</strong>,
        width: '30%'
    },
    {
        align: 'left',
        field: 'name',
        renderHeader: <strong>{i18n.t('Common.email')}</strong>,
        width: '30%'
    },
    {
        align: 'left',
        field: 'source',
        renderHeader: <strong>{i18n.t('Common.source')}</strong>,
        width: '20%'
    },
    {
        align: 'left',
        field: 'usage',
        renderHeader: <strong>{i18n.t('Common.usage')}</strong>,
        width: '15%'
    }
];

/*  State  */
const state = () => ({
    clientCount: 0,
    dataWasUpdated: false,
    map: {},
    notes: {
        community: [],
        partner: []
    },
    openNotesModal: false,
    openTagsModal: false,
    rerenderNotes: false,
    searchFields: [],
    searchVal: '',
    tableHeaders: [],
    tableParams: {},
    tagLinks: [],
    toggles: []
});

/*  Styles  */
const sxs = {
    grid: {
        setting: { paddingBottom: '12px' },
        userDetails: { paddingTop: '15px' }
    }
};

const renderAppCell = (row) => {
    const { application } = row;
    if (!application) {
        return <TableCell />;
    }

    return (
        <TableCell>
            <Application data={application}>
                <ApplicationNameLinkCell nameClassname="bold" withCategory />
            </Application>
        </TableCell>
    );
};

const renderRow = (row) => {
    const sources = Helpers.getSourcesWithActivity(row);

    return (
        <>
            <TableCell>
                <Rank usage={row.usage} ranking={row.ranking} />
            </TableCell>
            <TableCell>
                <User data={row.user}>
                    <UserIconAndName includeTitle />
                </User>
            </TableCell>
            <TableCell>
                <User data={row.user}>
                    <UserEmail />
                </User>
            </TableCell>
            <TableCell>
                <Sources sources={sources} noBadge />
            </TableCell>
            <TableCell>
                <Usage
                    usageChart={row.usageChart}
                    usage={row.usage}
                    sx={{ maxHeight: '40px' }}
                />
            </TableCell>
        </>
    );
};

const ClientTable = ({ handleRows, tenantApp }) => {
    const { applicationId, platformTenantId } = tenantApp;

    const parameters = useMemo(
        () => ({
            applicationId,
            includeActiveUser: true,
            includeUnclassified: true,
            platformTenantId,
            platformUserId: '*'
        }),
        [applicationId, platformTenantId]
    );

    const handleRowsFn = (rows) => {
        handleRows({ rows, tenantApp });
    };

    return (
        <ManagedTable
            handleRows={handleRowsFn}
            headers={headers}
            parameters={parameters}
            renderBody={renderRow}
            renderBodyKey="id"
            rowsApi="/userApp"
            rowsApiPreload
            rowsPerPage={-1}
            sortDirection="ASC"
            sortFieldType="number"
            sortKey="ranking"
        />
    );
};

class PartnerAppModal extends Component {
    constructor(props) {
        super(props);
        this.state = state();
        this.timeout = 0;

        const clientId = props?.match?.params?.clientId;
        /* requests */
        this.Note = new Note(clientId);
        this.TenantApp = new TenantApp(clientId);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = async () => {
        const { t, open, data } = this.props;
        if (!open) {
            return;
        }

        await this.loadNotes();
        await this.loadTagLinks();

        const { applicationId } = data;

        try {
            const clientLinksResponse = await axios.get('/clientLink', {
                params: {
                    excludeDisabled: true,
                    size: -1
                }
            });

            if (!Response.hasData(clientLinksResponse)) {
                debug.e('No Client Links Found');
                return;
            }

            const clientLinks = Response.getData(clientLinksResponse);

            // Extract tenant IDs from client links
            const tenantIds = clientLinks.map(
                (clientLink) => clientLink.linkedTenantId
            );

            const tenantIdsString = Payload.idString(tenantIds);

            const tenantApps = await this.TenantApp.getAll({
                applicationId,
                includeClients: true,
                includeTenant: true,
                platformTenantId: tenantIdsString
            });

            if (!tenantApps) {
                debug.e('No Tenant App Links Found', tenantApps);
                return;
            }

            const map = {};
            tenantApps.forEach((r) => {
                const { tenant, platformTenantId } = r;
                if (!tenant) {
                    return;
                }

                const { name } = tenant;
                map[platformTenantId] = {
                    ...r,
                    expanded: true,
                    loaded: false,
                    name,
                    show: true,
                    sources: {},
                    userCount: 0,
                    users: []
                };
            });

            const searchFields = ['id', 'type', 'message'];

            const toggles = [
                {
                    defaultEnabled: true,
                    key: 'archived',
                    parameter: { field: 'archived', value: false },
                    toggleText: t('Notes.exclude_archived')
                }
            ];

            const tableHeaders = [
                {
                    field: 'application.name',
                    renderHeader: t('Common.application'),
                    sortableKey: 'application.name',
                    width: '25%'
                },
                {
                    field: 'type',
                    renderHeader: t('Common.type'),
                    width: '25%'
                },
                {
                    field: 'message',
                    renderHeader: t('Common.note/recommendation'),
                    width: '30%'
                },
                {
                    align: 'right',
                    field: 'actions',
                    renderHeader: t('Common.actions'),
                    width: '20%'
                }
            ];

            const tableParams = {
                applicationId,
                includeApp: true
            };

            this.setState({
                clientCount: tenantApps.length,
                loaded: true,
                map,
                searchFields,
                tableHeaders,
                tableParams,
                toggles
            });
        } catch (e) {
            /* */
        }
    };

    loadNotes = async () => {
        const { data } = this.props;
        const { application } = data;
        const { id } = application;

        let partner;
        let community;

        try {
            partner = await this.Note.getPartner({
                applicationId: id,
                archived: false
            });
        } catch (e) {
            return;
        }

        try {
            community = await this.Note.getCommunity({
                applicationId: id,
                approved: true,
                includeImported: true
            });
        } catch (e) {
            return;
        }

        if (!this.unmounted) {
            this.setState({ notes: { community, partner } });
        }
    };

    rerenderNotes = () => {
        this.setState({ dataWasUpdated: true, rerenderNotes: true }, () => {
            this.loadNotes();
            this.setState({ rerenderNotes: false });
        });
    };

    loadTagLinks = async () => {
        const { data, match } = this.props;
        const { application } = data;
        const { partnerId } = match.params;

        let tagLinksResponse;
        try {
            const params = {
                applicationId: application.id,
                includeTag: true,
                includeTagSettings: true,
                platformTenantId: partnerId,
                size: -1
            };
            tagLinksResponse = await axios.get('/tagLink', {
                params
            });
        } catch (e) {
            return;
        }

        if (!Response.hasData(tagLinksResponse)) {
            return;
        }

        const tagLinks = Response.getData(tagLinksResponse);

        this.setState({ tagLinks });
    };

    unload = () => {
        this.unmount = true;
        if (this.timeout) clearTimeout(this.timeout);

        this.TenantApp.cancel();
    };

    handleSearch = (p) => {
        if (this.unmount) {
            return;
        }

        const { map } = this.state;
        const searchVal = p.target.value.toLowerCase();

        if (this.timeout) clearTimeout(this.timeout);

        const showIds = [];

        this.setState({ searchVal }, () => {
            this.timeout = setTimeout(() => {
                if (!searchVal) {
                    Object.keys(map).forEach((id) => {
                        map[id].show = true;
                        map[id].expanded = true;
                    });

                    this.setState({ map });
                    return;
                }

                Object.keys(map).forEach((id) => {
                    const { tenant } = map[id];
                    const tenantName = tenant?.name?.toLowerCase();

                    map[id]?.users?.forEach((u) => {
                        const { platformTenantId } = u;
                        if (showIds.includes(platformTenantId)) {
                            return;
                        }

                        const name = u.name.toLowerCase();
                        const email = u.email.toLowerCase();

                        if (
                            name?.includes(searchVal) ||
                            email?.includes(searchVal) ||
                            tenantName?.includes(searchVal)
                        ) {
                            showIds.push(platformTenantId);
                        }
                    });

                    if (showIds.includes(id)) {
                        map[id].show = true;
                        map[id].expanded = true;
                        return;
                    }

                    if (tenantName?.includes(searchVal)) {
                        map[id].show = true;
                        return;
                    }

                    map[id].show = false;
                    map[id].expanded = false;
                });

                this.setState({ map });
            }, 500);
        });
    };

    handleExpand = (p) => {
        if (this.unmount) {
            return;
        }

        const { map } = this.state;
        const { id, expanded } = p;

        map[id].expanded = expanded;
        this.setState({ map });
    };

    handleRows = ({ rows, tenantApp }) => {
        if (this.unmount) {
            return;
        }

        const { map } = this.state;
        const { platformTenantId } = tenantApp;

        if (!rows?.length) {
            return;
        }

        map[platformTenantId].loaded = true;
        map[platformTenantId].userCount = rows?.length;
        map[platformTenantId].users = [];

        const sources = Helpers.getConsolidatesSourcesFromUsers(rows);

        rows?.forEach((u) => {
            map[platformTenantId].users = [
                ...map[platformTenantId].users,
                u.user
            ];
            map[platformTenantId].sources = sources;
        });

        this.setState({ map });
    };

    renderClientCount = () => {
        const { t } = this.props;
        const { clientCount } = this.state;

        return (
            <DataCard title={t('Application.clientsUsing')}>
                <Typography
                    color="text.cardKPI"
                    variant="h1"
                    style={{ fontWeight: 500 }}
                >
                    {clientCount || 0}
                </Typography>
            </DataCard>
        );
    };

    renderUserCount = () => {
        const { t } = this.props;
        const { map } = this.state;

        const userCount = Object.values(map).reduce(
            (count, r) => count + r.userCount,
            0
        );

        return (
            <DataCard title={t('Application.usersUsing')}>
                <Typography
                    color="text.cardKPI"
                    variant="h1"
                    style={{ fontWeight: 500 }}
                >
                    {userCount || 0}
                </Typography>
            </DataCard>
        );
    };

    renderAppDetails = () => {
        const { t, data } = this.props;
        const { application, risk, status } = data;
        const { openTagsModal, tagLinks } = this.state;

        return (
            <Paper
                elevation={0}
                variant="outlined"
                style={{ height: '100%', minHeight: '316px' }}
            >
                <Application data={application}>
                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Grid item xs={12} style={sxs.grid.setting}>
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('Common.category')}
                                </Typography>
                                <ApplicationCategoryAndSubCategory
                                    noTruncate
                                    size="normal"
                                />
                            </Grid>
                            <Grid item xs={12} style={sxs.grid.setting}>
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('Common.risk')}
                                </Typography>
                                <RiskPicker visualOnly value={risk} />
                            </Grid>
                            <Grid item xs={12} style={sxs.grid.setting}>
                                <Typography
                                    color="textSecondary"
                                    variant="body2"
                                    style={{ fontWeight: 600 }}
                                >
                                    {t('Common.lifecycleStage')}
                                </Typography>
                                <LifecyclePicker visualOnly value={status} />
                            </Grid>
                            <Grid item xs={12} style={sxs.grid.setting}>
                                <Box display="flex" alignItems="center">
                                    <Typography
                                        color="textSecondary"
                                        fontWeight={600}
                                    >
                                        {t('Common.tags')}
                                    </Typography>
                                    <IconButton
                                        data-trackid="partner_appModal_openTagsModal"
                                        edge="end"
                                        size="small"
                                        color="secondary"
                                        disableFocusRipple
                                        onClick={() =>
                                            this.setState({
                                                openTagsModal: true
                                            })
                                        }
                                    >
                                        <Icon
                                            src="Pencil"
                                            size={FontSizes[2]}
                                        />
                                    </IconButton>
                                </Box>
                                {openTagsModal && (
                                    <EditTagLinksModal
                                        applicationId={application.id}
                                        currentTagLinks={tagLinks}
                                        open={openTagsModal}
                                        onClose={() => {
                                            this.loadTagLinks();
                                            this.setState({
                                                openTagsModal: false
                                            });
                                        }}
                                    />
                                )}
                                <TagLinksList tagLinks={tagLinks} />
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography
                                color="textSecondary"
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                {t('Common.description')}
                            </Typography>
                            <Box style={{ fontSize: FontSizes[1] }}>
                                <ApplicationDescription noTruncate />
                            </Box>
                        </Grid>
                    </Grid>
                </Application>
            </Paper>
        );
    };

    renderClient = (p) => {
        const { id } = p;
        const { t, data } = this.props;
        const { map } = this.state;

        const tenantApp = map[id];
        if (!tenantApp) {
            return null;
        }

        const { expanded, tenant, show } = tenantApp;

        if (!show) {
            return null;
        }

        const { application } = data;

        return (
            <Grid item xs={12} key={id}>
                <Accordion
                    expanded={expanded}
                    onChange={(_e, v) => this.handleExpand({ expanded: v, id })}
                    slotProps={{ transition: { unmountOnExit: true } }}
                >
                    <AccordionSummary
                        sx={{
                            alignItems: 'flex-start',
                            width: '100%'
                        }}
                        expandIcon={
                            <IconWithVariant
                                data-testid="expandAccordionIconPartnerAppModal"
                                variant="primary"
                                src="ChevronDown"
                                size={FontSizes[2]}
                                wrapperProps={{
                                    marginBottom: '20px',
                                    marginTop: '20px'
                                }}
                            />
                        }
                    >
                        <Grid container alignItems="center">
                            <Grid item xs={8}>
                                <Typography
                                    variant="span"
                                    sx={{
                                        fontSize: FontSizes[2],
                                        fontWeight: 600
                                    }}
                                    color="text.secondary"
                                >
                                    {tenant.name}
                                </Typography>
                            </Grid>
                            <Grid item xs={8} display="flex">
                                <Typography
                                    variant="span"
                                    color="textSecondary"
                                    style={{
                                        fontWeight: 600,
                                        marginTop: '4px',
                                        minWidth: '100px'
                                    }}
                                >
                                    {`${t('Common.ssoCoverage')}:`}
                                </Typography>
                                <SSOIcons data={data} noCount />
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                align="right"
                                sx={{ paddingRight: '8px' }}
                            >
                                <Mock
                                    id={tenant.id}
                                    page={`application/${application.id}`}
                                    button
                                >
                                    {t('Common.view_client')}
                                </Mock>
                            </Grid>
                        </Grid>
                    </AccordionSummary>
                    <AccordionDetails>
                        <ClientTable
                            handleRows={this.handleRows}
                            tenantApp={tenantApp}
                        />
                    </AccordionDetails>
                </Accordion>
            </Grid>
        );
    };

    archive = async (row) => {
        const { t } = this.props;
        const { archived, id } = row;
        debug.i('Archiving/Unarchiving App Note', row);

        try {
            const payload = {
                archived: !archived
            };

            await axios.put(`/partnerNote/${id}`, payload);
            if (archived) {
                Notify.s(t('Notes.unarchive_saved'));
            } else {
                Notify.s(t('Notes.archive_saved'));
            }
            this.rerenderNotes();

            // Reload the notes for the Notes modal
            await this.loadNotes();
        } catch (e) {
            if (archived) {
                debug.e(t('Notes.unarchive_error'), e);
                Notify.e(t('Notes.unarchive_error'));
            } else {
                debug.e(t('Notes.archive_error'), e);
                Notify.e(t('Notes.archive_error'));
            }
        }
    };

    renderNotesModal = () => {
        const { data, t } = this.props;
        const { application } = data;
        const { notes, openNotesModal } = this.state;

        return (
            <>
                <Box
                    display="flex"
                    justifyContent="flex-end"
                    width="100%"
                    mr="8px"
                    mt="8px"
                >
                    <Button
                        data-trackid="partner_appModal_notes_add"
                        color="primary"
                        variant="contained"
                        onClick={() => this.setState({ openNotesModal: true })}
                    >
                        <NoteAltOutlined style={{ marginRight: '4px' }} />
                        {t('Notes.add')}
                    </Button>
                </Box>
                {openNotesModal && (
                    <NotesModal
                        application={application}
                        communityNotes={notes.community}
                        partnerNotes={notes.partner}
                        modalOnly
                        open={openNotesModal}
                        partner
                        partnerHub
                        onSave={(reload) => {
                            if (reload) {
                                this.rerenderNotes();
                                this.loadNotes();
                            }
                            this.setState({ openNotesModal: false });
                        }}
                        onClose={() => this.setState({ openNotesModal: false })}
                    />
                )}
            </>
        );
    };

    renderActions = (row) => {
        const { t } = this.props;
        const { archived } = row;

        return (
            <Button
                data-trackid={
                    archived
                        ? 'partner_appModal_notes_unarchive'
                        : 'partner_appModal_notes_archive'
                }
                size="small"
                color="primary"
                variant="contained"
                onClick={() => this.archive(row)}
            >
                <AssignmentTurnedIn style={{ marginRight: '4px' }} />
                {archived ? t('Notes.unarchive') : t('Notes.archive')}
            </Button>
        );
    };

    renderNotesRow = (row) => {
        const { type, message } = row;

        return (
            <>
                {renderAppCell(row)}
                <TableCell>{type}</TableCell>
                <TableCell>{message}</TableCell>
                <TableCell align="right">{this.renderActions(row)}</TableCell>
            </>
        );
    };

    renderNotesTable = () => {
        const { t } = this.props;
        const {
            tableParams,
            rerenderNotes,
            searchFields,
            tableHeaders,
            toggles
        } = this.state;

        return (
            <Paper elevation={0} variant="outlined">
                <Typography variant="h5">
                    {t('Common.notes_and_recommendations')}
                </Typography>
                {this.renderNotesModal()}
                <ManagedTable
                    headers={tableHeaders}
                    parameters={tableParams}
                    renderBody={this.renderNotesRow}
                    renderBodyKey="id"
                    renderFooter
                    rerender={rerenderNotes}
                    rowsApi="/partnerNote"
                    rowsApiPreload
                    searchEnabled
                    searchFields={searchFields}
                    sortDirection="ASC"
                    sortKey="application.name"
                    toggles={toggles}
                />
            </Paper>
        );
    };

    renderUserDetails = () => {
        const { t } = this.props;
        const { map, searchVal } = this.state;

        return (
            <Grid container spacing={2}>
                <Grid item xs={12} style={sxs.grid.userDetails}>
                    <Typography variant="h5">
                        {t('Common.user_details')}
                    </Typography>
                    <TextField
                        value={searchVal}
                        onChange={(event) => this.handleSearch(event)}
                        autoComplete="off"
                        fullWidth
                        label={
                            <>
                                <Search fontSize="small" />
                                {t('Search.client_or_user')}
                            </>
                        }
                    />
                </Grid>
                {Object.values(map).map((r) =>
                    this.renderClient({ id: r.platformTenantId })
                )}
            </Grid>
        );
    };

    renderContent = () => (
        <Grid container spacing={1} alignItems="stretch">
            <Grid item xs={12} lg={8}>
                {this.renderAppDetails()}
            </Grid>
            <Grid item xs={12} lg={2}>
                {this.renderClientCount()}
            </Grid>
            <Grid item xs={12} lg={2}>
                {this.renderUserCount()}
            </Grid>
            <Grid item xs={12} marginTop="16px">
                {this.renderNotesTable()}
            </Grid>
            <Grid item xs={12} marginTop="16px">
                {this.renderUserDetails()}
            </Grid>
        </Grid>
    );

    renderModal = () => {
        const { t, data, onCloseModal } = this.props;
        const { dataWasUpdated } = this.state;

        return (
            <>
                <DialogTitle sx={{ backgroundColor: 'background.tertiary' }}>
                    {/* Re: noEdit. You need a clientId to be able to edit the name, which is null at partner level.  */}
                    <TenantAppContextProvider
                        applicationId={data.applicationId}
                    >
                        <AppHeader
                            hideSSOCoverage
                            modalView
                            noEdit
                            onClose={onCloseModal}
                        />
                    </TenantAppContextProvider>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{ backgroundColor: 'background.primary' }}
                >
                    {this.renderContent()}
                </DialogContent>
                <DialogActions>
                    <Button
                        data-trackid="partner_appModal_close"
                        color="primary"
                        variant="contained"
                        style={{ float: 'right' }}
                        onClick={() => {
                            onCloseModal(dataWasUpdated);
                        }}
                    >
                        {t('Common.close')}
                    </Button>
                </DialogActions>
            </>
        );
    };

    render() {
        const { open, onCloseModal } = this.props;
        const { dataWasUpdated, loaded } = this.state;

        if (!open || !loaded) {
            return null;
        }

        return (
            <Dialog
                data-trackid="sharedModalsPartnerAppModalDialog"
                fullScreen
                PaperProps={{ style: { padding: 0 } }}
                onClose={() => {
                    onCloseModal(dataWasUpdated);
                }}
                open={open}
                TransitionComponent={Transition}
            >
                {this.renderModal()}
            </Dialog>
        );
    }
}

export default withRouter(withTranslation()(PartnerAppModal));
